import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowLeft, Mail } from "lucide-react";
import SEO from "../components/SEO";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Privacy Policy"
        description="RNB Consulting Group's privacy policy. Learn how we collect, use, and protect your personal information when you use our website and services."
        keywords="RNB Consulting privacy policy, healthcare consulting privacy, data protection"
        noIndex={false}
      />
      
      {/* Header */}
      <section className="py-16 bg-gradient-to-br from-rnb-light to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center text-rnb-blue hover:text-rnb-navy mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-rnb-navy mb-4">
            Privacy Policy
          </h1>
          <p className="text-rnb-gray">
            <strong>Effective Date:</strong> January 10, 2026
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-slate max-w-none">
            <p className="text-rnb-gray leading-relaxed mb-8">
              RNB Consulting Group (&ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) is committed to protecting your privacy. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
              when you visit our website, use our client portal, or engage with our services.
            </p>

            <h2 className="font-heading text-2xl font-bold text-rnb-navy mt-12 mb-4">
              1. Information We Collect
            </h2>
            <p className="text-rnb-gray mb-4">
              We may collect information about you in a variety of ways, including:
            </p>
            <h3 className="font-heading text-lg font-semibold text-rnb-navy mt-6 mb-3">
              Personal Data
            </h3>
            <p className="text-rnb-gray mb-4">
              When you register for an account, submit a contact form, or engage our services, we may collect 
              personally identifiable information such as:
            </p>
            <ul className="list-disc pl-6 text-rnb-gray mb-4 space-y-2">
              <li>Name</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Company/Organization name</li>
              <li>Billing and payment information</li>
              <li>Professional credentials (for program applicants)</li>
            </ul>
            
            <h3 className="font-heading text-lg font-semibold text-rnb-navy mt-6 mb-3">
              Automatically Collected Data
            </h3>
            <p className="text-rnb-gray mb-4">
              When you visit our website, we may automatically collect certain information, including:
            </p>
            <ul className="list-disc pl-6 text-rnb-gray mb-4 space-y-2">
              <li>IP address</li>
              <li>Browser type and version</li>
              <li>Operating system</li>
              <li>Pages visited and time spent</li>
              <li>Referring website addresses</li>
              <li>Device identifiers</li>
            </ul>

            <h2 className="font-heading text-2xl font-bold text-rnb-navy mt-12 mb-4">
              2. How We Use Your Information
            </h2>
            <p className="text-rnb-gray mb-4">
              We use the information we collect for purposes including:
            </p>
            <ul className="list-disc pl-6 text-rnb-gray mb-4 space-y-2">
              <li>Providing, operating, and maintaining our services</li>
              <li>Processing transactions and sending related information</li>
              <li>Responding to your inquiries and support requests</li>
              <li>Sending administrative information (updates, security alerts)</li>
              <li>Sending marketing communications (with your consent)</li>
              <li>Improving our website and services</li>
              <li>Analyzing usage trends and user preferences</li>
              <li>Complying with legal obligations</li>
            </ul>

            <h2 className="font-heading text-2xl font-bold text-rnb-navy mt-12 mb-4">
              3. Information Sharing and Disclosure
            </h2>
            <p className="text-rnb-gray mb-4">
              We do not sell your personal information. We may share your information in the following situations:
            </p>
            <ul className="list-disc pl-6 text-rnb-gray mb-4 space-y-2">
              <li><strong>Service Providers:</strong> Third-party vendors who perform services on our behalf (payment processing, email delivery, analytics)</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
              <li><strong>Legal Compliance:</strong> When required by law or to protect our rights</li>
              <li><strong>With Your Consent:</strong> When you have given us explicit permission</li>
            </ul>

            <h2 className="font-heading text-2xl font-bold text-rnb-navy mt-12 mb-4">
              4. Data Security
            </h2>
            <p className="text-rnb-gray mb-4">
              We implement appropriate technical and organizational security measures to protect your personal information, including:
            </p>
            <ul className="list-disc pl-6 text-rnb-gray mb-4 space-y-2">
              <li>Encryption of data in transit (SSL/TLS) and at rest (AES-256)</li>
              <li>Secure authentication mechanisms</li>
              <li>Regular security assessments</li>
              <li>Access controls and audit logging</li>
              <li>Employee training on data protection</li>
            </ul>
            <p className="text-rnb-gray mb-4">
              However, no method of transmission over the Internet or electronic storage is 100% secure. 
              While we strive to protect your information, we cannot guarantee absolute security.
            </p>

            <h2 className="font-heading text-2xl font-bold text-rnb-navy mt-12 mb-4">
              5. Cookies and Tracking Technologies
            </h2>
            <p className="text-rnb-gray mb-4">
              We use cookies and similar tracking technologies to collect information about your browsing activities. 
              These may include:
            </p>
            <ul className="list-disc pl-6 text-rnb-gray mb-4 space-y-2">
              <li><strong>Essential Cookies:</strong> Required for the website to function properly</li>
              <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our site (e.g., Google Analytics)</li>
              <li><strong>Marketing Cookies:</strong> Used to deliver relevant advertisements (e.g., Meta Pixel)</li>
            </ul>
            <p className="text-rnb-gray mb-4">
              You can control cookies through your browser settings. Disabling certain cookies may affect your experience on our website.
            </p>

            <h2 className="font-heading text-2xl font-bold text-rnb-navy mt-12 mb-4">
              6. Third-Party Services
            </h2>
            <p className="text-rnb-gray mb-4">
              Our website may contain links to third-party websites and services, including:
            </p>
            <ul className="list-disc pl-6 text-rnb-gray mb-4 space-y-2">
              <li>Payment processors (Stripe)</li>
              <li>Scheduling tools (Calendly)</li>
              <li>Analytics services (Google Analytics)</li>
              <li>Email services (SendGrid)</li>
            </ul>
            <p className="text-rnb-gray mb-4">
              These third parties have their own privacy policies, and we encourage you to review them. 
              We are not responsible for the privacy practices of third-party services.
            </p>

            <h2 className="font-heading text-2xl font-bold text-rnb-navy mt-12 mb-4">
              7. Your Rights and Choices
            </h2>
            <p className="text-rnb-gray mb-4">
              Depending on your location, you may have certain rights regarding your personal information:
            </p>
            <ul className="list-disc pl-6 text-rnb-gray mb-4 space-y-2">
              <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
              <li><strong>Correction:</strong> Request that we correct inaccurate or incomplete information</li>
              <li><strong>Deletion:</strong> Request that we delete your personal information</li>
              <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications at any time</li>
              <li><strong>Data Portability:</strong> Request a copy of your data in a machine-readable format</li>
            </ul>
            <p className="text-rnb-gray mb-4">
              To exercise any of these rights, please contact us at{" "}
              <a href="mailto:info@rnbconsultinggroup.com" className="text-rnb-blue hover:underline">
                info@rnbconsultinggroup.com
              </a>.
            </p>

            <h2 className="font-heading text-2xl font-bold text-rnb-navy mt-12 mb-4">
              8. Data Retention
            </h2>
            <p className="text-rnb-gray mb-4">
              We retain your personal information for as long as necessary to fulfill the purposes described 
              in this Privacy Policy, unless a longer retention period is required by law. When we no longer 
              need your information, we will securely delete or anonymize it.
            </p>

            <h2 className="font-heading text-2xl font-bold text-rnb-navy mt-12 mb-4">
              9. Children&apos;s Privacy
            </h2>
            <p className="text-rnb-gray mb-4">
              Our services are not directed to individuals under the age of 18. We do not knowingly collect 
              personal information from children. If we become aware that we have collected personal information 
              from a child, we will take steps to delete that information.
            </p>

            <h2 className="font-heading text-2xl font-bold text-rnb-navy mt-12 mb-4">
              10. Changes to This Privacy Policy
            </h2>
            <p className="text-rnb-gray mb-4">
              We may update this Privacy Policy from time to time. Changes will be posted on this page with a 
              new effective date. We encourage you to review this Privacy Policy periodically.
            </p>

            <h2 className="font-heading text-2xl font-bold text-rnb-navy mt-12 mb-4">
              11. Contact Us
            </h2>
            <p className="text-rnb-gray mb-4">
              If you have questions or concerns about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="bg-slate-50 rounded-lg p-6 mt-4">
              <p className="font-semibold text-rnb-navy mb-2">RNB Consulting Group</p>
              <p className="text-rnb-gray">Atlanta, GA, USA</p>
              <p className="text-rnb-gray">
                Email:{" "}
                <a href="mailto:info@rnbconsultinggroup.com" className="text-rnb-blue hover:underline">
                  info@rnbconsultinggroup.com
                </a>
              </p>
              <p className="text-rnb-gray">
                Phone:{" "}
                <a href="tel:888-250-4605" className="text-rnb-blue hover:underline">
                  (888) 250-4605
                </a>
              </p>
            </div>

            <div className="mt-12 pt-8 border-t text-center">
              <p className="text-rnb-gray mb-4">
                By using our services, you acknowledge that you have read and understand this Privacy Policy.
              </p>
              <Link to="/contact">
                <Button className="btn-hover-lift">
                  <Mail className="mr-2 h-4 w-4" />
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
